import { useCallback } from 'react';
import { Clear, Refresh } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { Datepicker, Select, Tooltip } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { DOD_COMMAND_OPTIONS } from '../dodCommands';
import { SegmentedControl } from '../segmentedControl/SegmentedControl';
import { DodRangePreset, DodRefreshOption } from '../../hooks/useDodFilters/useDodFilters.types';

import { GrafanaBarProps } from './GrafanaBar.types';
import * as Styled from './GrafanaBar.styles';

const RANGE_PRESETS: Array<Exclude<DodRangePreset, 'custom'>> = ['15m', '1h', '6h', '24h', '7d'];
const REFRESH_OPTIONS: DodRefreshOption[] = ['off', '10s', '30s', '1m'];

const toDate = (value: string): Date | null => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * The toolbar every DOD tab sits under: time range, auto-refresh and the four cross-tab filters.
 *
 * It owns no state. Everything is read from and written to the URL through `useDodFilters()`, so
 * the bar renders the same way after a reload and a pasted link reproduces the view.
 */
export const GrafanaBar = ({
  filters,
  rcpOptions = [],
  tkdOptions = [],
  statusOptions = [],
  onRefresh,
}: GrafanaBarProps): JSX.Element => {
  const { formatMessage } = useLocale();
  const {
    range,
    refresh,
    filters: values,
    hasFilters,
    customFrom,
    customTo,
    setRange,
    setRefresh,
    setFilter,
    clear,
  } = filters;

  const allLabel = formatMessage({ id: AppMessages['dod.toolbar.all'] });

  const handleCustomFrom = useCallback(
    (date: Date | null | ''): void => setRange('custom', { from: date || null, to: toDate(customTo) }),
    [setRange, customTo],
  );

  const handleCustomTo = useCallback(
    (date: Date | null | ''): void => setRange('custom', { from: toDate(customFrom), to: date || null }),
    [setRange, customFrom],
  );

  /** "wszystkie" is a real option, so the operator can pick it back after narrowing the list. */
  const withAll = (options: GrafanaBarProps['rcpOptions'] = []): Array<{ label: string; value: string }> => [
    { label: allLabel, value: '' },
    ...options,
  ];

  const rangeOptions = [
    ...RANGE_PRESETS.map((preset) => ({ value: preset, label: preset })),
    { value: 'custom' as const, label: formatMessage({ id: AppMessages['dod.toolbar.range.custom'] }) },
  ];

  return (
    <Styled.Bar role="toolbar" aria-label={formatMessage({ id: AppMessages['dod.toolbar.label'] })}>
      <Styled.RangeField>
        <SegmentedControl<DodRangePreset>
          label={formatMessage({ id: AppMessages['dod.toolbar.range'] })}
          value={range}
          options={rangeOptions}
          onChange={(preset) => setRange(preset)}
        />
      </Styled.RangeField>

      {range === 'custom' && (
        <>
          <Datepicker
            value={toDate(customFrom)}
            onChange={handleCustomFrom}
            withCalendarIcon
            withoutHelperText
            inputVariant="outlined"
            placeholderText={formatMessage({ id: AppMessages['dod.toolbar.range.from'] })}
            data-testid="dod-range-from"
          />
          <Datepicker
            value={toDate(customTo)}
            onChange={handleCustomTo}
            withCalendarIcon
            withoutHelperText
            inputVariant="outlined"
            placeholderText={formatMessage({ id: AppMessages['dod.toolbar.range.to'] })}
            data-testid="dod-range-to"
          />
        </>
      )}

      <Styled.FieldWithAction>
        <Select
          label={formatMessage({ id: AppMessages['dod.toolbar.refresh'] })}
          variant="outlined"
          testId="dod-refresh-select"
          value={refresh}
          onChange={(event) => setRefresh(event.target.value as DodRefreshOption)}
          options={REFRESH_OPTIONS.map((option) => ({
            value: option,
            label:
              option === 'off'
                ? formatMessage({ id: AppMessages['dod.toolbar.refresh.off'] })
                : option.replace('s', ' s'),
          }))}
        />
        <Tooltip title={formatMessage({ id: AppMessages['dod.refresh'] })} placement="top">
          <IconButton onClick={onRefresh} aria-label={formatMessage({ id: AppMessages['dod.refresh'] })}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Styled.FieldWithAction>

      <Select
        label={formatMessage({ id: AppMessages['dod.toolbar.filter.rcp'] })}
        variant="outlined"
        testId="dod-filter-rcp"
        value={values.rcp}
        placeholder={allLabel}
        onChange={(event) => setFilter('rcp', String(event.target.value))}
        options={withAll(rcpOptions)}
      />

      <Select
        label={formatMessage({ id: AppMessages['dod.toolbar.filter.tkd'] })}
        variant="outlined"
        testId="dod-filter-nlk"
        value={values.nlk}
        placeholder={allLabel}
        onChange={(event) => setFilter('nlk', String(event.target.value))}
        options={withAll(tkdOptions)}
      />

      <Select
        label={formatMessage({ id: AppMessages['dod.toolbar.filter.command'] })}
        variant="outlined"
        testId="dod-filter-cmd"
        value={values.cmd}
        placeholder={allLabel}
        onChange={(event) => setFilter('cmd', String(event.target.value))}
        options={withAll(DOD_COMMAND_OPTIONS)}
      />

      {statusOptions.length > 0 && (
        <Select
          label={formatMessage({ id: AppMessages['dod.toolbar.filter.status'] })}
          variant="outlined"
          testId="dod-filter-status"
          value={values.status}
          placeholder={allLabel}
          onChange={(event) => setFilter('status', String(event.target.value))}
          options={withAll(statusOptions)}
        />
      )}

      {hasFilters && (
        <Styled.Action>
          <Tooltip title={formatMessage({ id: AppMessages['dod.toolbar.filter.clear'] })} placement="top">
            <IconButton onClick={clear} aria-label={formatMessage({ id: AppMessages['dod.toolbar.filter.clear'] })}>
              <Clear />
            </IconButton>
          </Tooltip>
        </Styled.Action>
      )}
    </Styled.Bar>
  );
};
